import { useEffect, useMemo, useState } from 'react';
import { Lock, ShieldCheck } from 'lucide-react';
import { useApp } from '@/store';
import { useT, type I18nKey } from '@/lib/i18n';
import {
  ACTIONS,
  ADMIN_SECTIONS,
  MatrixRole,
  PermAction,
  SectionId,
  WORK_SECTIONS,
  configuredRights,
  defaultRights,
  hasAll,
  isApplicable,
  maxRights,
  setAll,
  setRight,
  toStored,
  type SectionRight,
  type SectionRights,
} from '@/lib/section-permissions';
import { PageHeader } from '@/components/layout/PageHeader';
import { ScrollTable } from '@/components/layout/ScrollTable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

// Verwaltung → Berechtigungen (client #95). The page is a stack of cards, each
// owning its own state and save, so further permission settings can be added
// as another card below the matrix without touching it.
export function Permissions() {
  const t = useT();
  return (
    <div className="space-y-4">
      <PageHeader title={t('perms.title')} subtitle={t('perms.subtitle')} />
      <SectionRightsCard />
      <TechVisibilityCard />
    </div>
  );
}

// Client #92: technicians only receive the customers assigned to them in the
// calendar; these two boxes widen that for the whole Techniker role. The
// server's sync proxy enforces it, so it applies whatever the device runs.
function TechVisibilityCard() {
  const t = useT();
  const stored = useApp(s => s.settings.techVisibility);
  const updateSettings = useApp(s => s.updateSettings);
  const toast = useApp(s => s.toast);
  const [draft, setDraft] = useState(() => ({
    dueSoon: !!stored?.dueSoon,
    all: !!stored?.all,
  }));
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!dirty) setDraft({ dueSoon: !!stored?.dueSoon, all: !!stored?.all });
  }, [stored, dirty]);

  const toggle = (key: 'dueSoon' | 'all', v: boolean) => {
    setDraft(cur => ({ ...cur, [key]: v }));
    setDirty(true);
  };

  const save = () => {
    updateSettings({ techVisibility: draft });
    setDirty(false);
    toast(t('perms.saved'));
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('perms.techScopeTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t('perms.techScopeIntro')}
        </p>
        <label className="flex items-center gap-3 min-h-11 cursor-pointer">
          <Checkbox
            checked={draft.dueSoon || draft.all}
            disabled={draft.all}
            onCheckedChange={v => toggle('dueSoon', v === true)}
            data-testid="tech-scope-due-soon"
          />
          <span className="text-sm">{t('perms.techScopeDueSoon')}</span>
        </label>
        <label className="flex items-center gap-3 min-h-11 cursor-pointer">
          <Checkbox
            checked={draft.all}
            onCheckedChange={v => toggle('all', v === true)}
            data-testid="tech-scope-all"
          />
          <span className="text-sm">{t('perms.techScopeAll')}</span>
        </label>
        <p className="text-xs text-muted-foreground">
          {t('perms.techScopeApply')}
        </p>
        <Button
          className="h-11 sm:h-9"
          onClick={save}
          disabled={!dirty}
          data-testid="tech-scope-save"
        >
          {t('perms.save')}
        </Button>
      </CardContent>
    </Card>
  );
}

const ROLES: readonly MatrixRole[] = [MatrixRole.TECHNICIAN, MatrixRole.STAFF];

const ROLE_LABEL: Record<MatrixRole, I18nKey> = {
  [MatrixRole.TECHNICIAN]: 'perms.roleTech',
  [MatrixRole.STAFF]: 'perms.roleStaff',
};

const SECTION_LABEL: Record<SectionId, I18nKey> = {
  [SectionId.CUSTOMERS]: 'perms.sec.customers',
  [SectionId.EXTINGUISHERS]: 'perms.sec.extinguishers',
  [SectionId.HOSES]: 'perms.sec.hoses',
  [SectionId.MAINTENANCE]: 'perms.sec.maintenance',
  [SectionId.CALENDAR]: 'perms.sec.calendar',
  [SectionId.MAP]: 'perms.sec.map',
  [SectionId.REMINDERS]: 'perms.sec.reminders',
  [SectionId.REPORTS]: 'perms.sec.reports',
  [SectionId.INVOICES]: 'perms.sec.invoices',
  [SectionId.MANUFACTURERS]: 'perms.sec.manufacturers',
  [SectionId.DEVICE_TYPES]: 'perms.sec.deviceTypes',
  [SectionId.SPARE_PARTS]: 'perms.sec.spareParts',
  [SectionId.SERVICE_PRICES]: 'perms.sec.servicePrices',
  [SectionId.HOSE_OPTIONS]: 'perms.sec.hoseOptions',
  [SectionId.WORKERS]: 'perms.sec.workers',
  [SectionId.SETTINGS]: 'perms.sec.settings',
};

// Where Erstellen / Bearbeiten do not mean the obvious thing, say what they do.
const SECTION_HINT: Partial<Record<SectionId, I18nKey>> = {
  [SectionId.MAINTENANCE]: 'perms.hintMaintenance',
  [SectionId.INVOICES]: 'perms.hintInvoices',
};

const ACTION_LABEL: Record<PermAction, I18nKey> = {
  [PermAction.READ]: 'perms.colRead',
  [PermAction.CREATE]: 'perms.colCreate',
  [PermAction.EDIT]: 'perms.colEdit',
  [PermAction.DELETE]: 'perms.colDelete',
};

type Matrix = Record<MatrixRole, SectionRights>;

const seedFrom = (
  settings: Parameters<typeof configuredRights>[1]
): Matrix => ({
  [MatrixRole.TECHNICIAN]: configuredRights(MatrixRole.TECHNICIAN, settings),
  [MatrixRole.STAFF]: configuredRights(MatrixRole.STAFF, settings),
});

function SectionRightsCard() {
  const t = useT();
  const permissions = useApp(s => s.settings.permissions);
  const permMatrix = useApp(s => s.settings.permMatrix);
  const updateSettings = useApp(s => s.updateSettings);
  const toast = useApp(s => s.toast);

  const [role, setRole] = useState<MatrixRole>(MatrixRole.TECHNICIAN);
  const [draft, setDraft] = useState<Matrix>(() =>
    seedFrom({ permissions, permMatrix })
  );
  const [dirty, setDirty] = useState(false);
  const max = useMemo<Matrix>(
    () => ({
      [MatrixRole.TECHNICIAN]: maxRights(MatrixRole.TECHNICIAN),
      [MatrixRole.STAFF]: maxRights(MatrixRole.STAFF),
    }),
    []
  );

  // Settings can arrive after the page mounts (first sync, another admin
  // saving elsewhere). Follow them until the user starts editing.
  useEffect(() => {
    if (!dirty) setDraft(seedFrom({ permissions, permMatrix }));
  }, [permissions, permMatrix, dirty]);

  const updateRow = (section: SectionId, next: SectionRight) => {
    setDraft(cur => ({
      ...cur,
      [role]: { ...cur[role], [section]: next },
    }));
    setDirty(true);
  };

  const save = () => {
    updateSettings({ permissions: toStored(draft) });
    setDirty(false);
    toast(t('perms.saved'));
  };

  // Restores the visible role only; nothing is stored until Save.
  const reset = () => {
    setDraft(cur => ({ ...cur, [role]: defaultRights(role) }));
    setDirty(true);
  };

  const rows = (sections: readonly SectionId[]) =>
    sections.map(s => (
      <SectionRow
        key={s}
        section={s}
        right={draft[role][s]}
        max={max[role][s]}
        onChange={next => updateRow(s, next)}
      />
    ));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('perms.matrixTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 shrink-0" />
          {t('perms.note')}
        </p>
        <Tabs value={role} onValueChange={v => setRole(v as MatrixRole)}>
          <TabsList className="h-11">
            {ROLES.map(r => (
              <TabsTrigger
                key={r}
                value={r}
                className="h-9 px-4"
                data-testid={`perms-role-${r}`}
              >
                {t(ROLE_LABEL[r])}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <ScrollTable minWidth={400}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 z-10 bg-card">
                  {t('perms.section')}
                </TableHead>
                <TableHead className="text-center">
                  {t('perms.colAll')}
                </TableHead>
                {ACTIONS.map(a => (
                  <TableHead key={a} className="text-center">
                    {t(ACTION_LABEL[a])}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              <GroupRow label={t('nav.main')} />
              {rows(WORK_SECTIONS)}
              <GroupRow label={t('nav.admin')} />
              {rows(ADMIN_SECTIONS)}
            </TableBody>
          </Table>
        </ScrollTable>
        <p className="text-xs text-muted-foreground flex items-center gap-2">
          <Lock className="h-3.5 w-3.5 shrink-0" />
          {t('perms.adminOnlyHint')}
        </p>
        <div className="flex gap-2 flex-wrap">
          <Button
            className="h-11 sm:h-9"
            onClick={save}
            data-testid="perms-save"
          >
            {t('perms.save')}
          </Button>
          <Button variant="outline" className="h-11 sm:h-9" onClick={reset}>
            {t('perms.reset')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function GroupRow({ label }: { label: string }) {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell
        colSpan={6}
        className="bg-muted/40 py-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground"
      >
        {label}
      </TableCell>
    </TableRow>
  );
}

function SectionRow({
  section,
  right,
  max,
  onChange,
}: {
  section: SectionId;
  right: SectionRight;
  max: SectionRight;
  onChange: (next: SectionRight) => void;
}) {
  const t = useT();
  const label = t(SECTION_LABEL[section]);
  const hint = SECTION_HINT[section];
  return (
    <TableRow data-testid={`perms-row-${section}`}>
      <TableCell className="sticky left-0 z-10 bg-card py-1">
        <div className="text-sm font-medium">{label}</div>
        {hint && (
          <div className="text-[11px] leading-tight text-muted-foreground">
            {t(hint)}
          </div>
        )}
      </TableCell>
      <RightCell
        label={`${t('perms.colAll')}: ${label}`}
        checked={hasAll(right, max)}
        onChange={v => onChange(setAll(v, max))}
      />
      {ACTIONS.map(a =>
        isApplicable(section, a) ? (
          <RightCell
            key={a}
            label={`${t(ACTION_LABEL[a])}: ${label}`}
            checked={right[a]}
            disabled={!max[a]}
            onChange={v => onChange(setRight(right, a, v, max))}
          />
        ) : (
          <TableCell
            key={a}
            className="text-center text-muted-foreground"
            title={t('perms.notApplicable')}
          >
            <span aria-hidden>–</span>
            <span className="sr-only">{t('perms.notApplicable')}</span>
          </TableCell>
        )
      )}
    </TableRow>
  );
}

function RightCell({
  label,
  checked,
  disabled = false,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}) {
  const t = useT();
  return (
    <TableCell className="p-0 text-center">
      {/* The label is the 44px touch target around the small checkbox. */}
      <label
        className={cn(
          'inline-flex h-11 w-11 items-center justify-center',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        )}
        title={disabled ? t('perms.adminOnly') : label}
      >
        <Checkbox
          checked={checked}
          disabled={disabled}
          aria-label={disabled ? `${label} (${t('perms.adminOnly')})` : label}
          onCheckedChange={v => onChange(v === true)}
        />
      </label>
    </TableCell>
  );
}
